import moment from 'moment'

import { ARCADE_BADGE_TYPES, ARCADE_RANKS } from '@/enum'

import * as Arcade from './types'
import { isNull, isNullOrUndefined, isUndefined } from '@/util/typeHelper'
import { ArcadeUser } from '@/api/resources/shared/account'
import { User } from '@/api/resources/shared/user'
import { CountdownGranularity, formattedCountdown } from '@/util/time'
import { ArcadeSeason, SeasonStatus } from './types'
import { calcTimeRemaining } from '@/contexts/countdown'

type ArcadeRankType = (typeof ARCADE_RANKS)[keyof typeof ARCADE_RANKS]

export const determineRank = (
  rank: number | null | undefined,
  threshold: number = 333
): ArcadeRankType => {
  if (isNullOrUndefined(rank) || rank > threshold)
    return ARCADE_RANKS.Ineligible

  return ARCADE_RANKS.Eligible
}

export const FIRST_SEASON_ID = 1 as const

export const buildSeason = (arcadeSeason: Arcade.ArcadeSeason) => {
  const isCurrentSeason = arcadeSeason.season_status === 'current'
  const isUpcomingSeason = arcadeSeason.season_status === 'next'
  const isPastSeason = arcadeSeason.season_status === 'past'
  const isActiveSeason = isCurrentSeason || isUpcomingSeason

  const isFirstUpcomingSeason =
    isUpcomingSeason && arcadeSeason.id === FIRST_SEASON_ID

  return {
    id: arcadeSeason.id,
    name: arcadeSeason.name,
    status: arcadeSeason.season_status,
    startAt: arcadeSeason.time_start,
    endAt: arcadeSeason.time_end,
    rankThreshold: arcadeSeason.rank_threshold,
    convertLimit: arcadeSeason.convert_limit,
    isCurrentSeason,
    isUpcomingSeason,
    isActiveSeason,
    isPastSeason,
    isFirstUpcomingSeason
  }
}

export const buildPlayer = (
  user: User,
  arcadeUser: Pick<ArcadeUser, 'player_rank'>,
  season?: Arcade.Season
): Arcade.Player | null => {
  const playerRank = arcadeUser.player_rank
  if (isUndefined(playerRank)) return null

  const isNotRanked = isNull(playerRank)
  const isNotPlaying = isNotRanked && season?.isCurrentSeason

  return {
    account: user.account_wax,
    rank: playerRank,
    isNotRanked,
    isRanked: !isNotRanked,
    isNotPlaying,
    isPlaying: !isNotPlaying
  }
}

export const calculateRemainingSeconds = (
  timestamp?: Arcade.Timestamp
): Arcade.Seconds => {
  if (isNullOrUndefined(timestamp)) return 0
  return Math.max(calcTimeRemaining(timestamp!), 0)
}

const SECONDS_IN_AN_HOUR = 3600
const SECONDS_TO_DAYS_FACTOR = 3600 * 24
const SECONDS_TO_MINUTES_FACTOR = 0.017

export type RemainingPrecision = 'high' | 'medium' | 'low'

const formatLowPrecision = (days: number, minutes: number, seconds: number) => {
  if (days >= 1) {
    return formattedCountdown(
      moment.duration(days, 'days'),
      CountdownGranularity.Days
    )
  }

  if (seconds <= SECONDS_IN_AN_HOUR) {
    return formattedCountdown(
      moment.duration(seconds, 'seconds'),
      CountdownGranularity.Seconds
    )
  }

  return formattedCountdown(
    moment.duration(minutes, 'minutes'),
    CountdownGranularity.Hours
  )
}

const formatMediumPrecision = (days: number, seconds: number) => {
  const formattedResult = []

  if (days >= 1) {
    const daysDuration = moment.duration(days, 'days').asDays()
    formattedResult.push(`${daysDuration}D`)
  }

  if (seconds <= SECONDS_IN_AN_HOUR) {
    formattedResult.push(
      formattedCountdown(
        moment.duration(seconds, 'seconds'),
        CountdownGranularity.Seconds
      )
    )
  } else {
    formattedResult.push(
      formattedCountdown(
        moment.duration(seconds, 'seconds'),
        CountdownGranularity.Hours
      )
    )
  }

  return formattedResult.join(' ')
}

const formatHighPrecision = (days: number, seconds: number) => {
  const formattedResult = []

  if (days >= 1) {
    const daysDuration = moment.duration(days, 'days').asDays()
    formattedResult.push(`${daysDuration}D`)
  }

  formattedResult.push(
    formattedCountdown(
      moment.duration(seconds, 'seconds'),
      CountdownGranularity.Seconds
    )
  )

  return formattedResult.join(' ')
}

export const formatRemainingSeconds = (
  seconds: number,
  precision: RemainingPrecision = 'high',
  none = 'None'
) => {
  const days = Math.floor(seconds / SECONDS_TO_DAYS_FACTOR)
  const minutes = Math.floor(seconds * SECONDS_TO_MINUTES_FACTOR)

  if (seconds <= 0) return none

  if (precision === 'low') {
    return formatLowPrecision(days, minutes, seconds)
  }

  if (precision === 'medium') {
    return formatMediumPrecision(days, seconds)
  }

  return formatHighPrecision(days, seconds)
}

type RankToBadge = {
  [K in keyof typeof ARCADE_RANKS]: (typeof ARCADE_BADGE_TYPES)[K]
}

export const rankToBadge: RankToBadge = {
  Eligible: ARCADE_BADGE_TYPES.Eligible,
  Ineligible: ARCADE_BADGE_TYPES.Ineligible
}

const findFirstSeasonByStatus = (
  status: SeasonStatus,
  arcadeSeasons: ArcadeSeason[] = []
) => arcadeSeasons.find(season => season.season_status === status)

const findLastSeasonByStatus = (
  status: SeasonStatus,
  arcadeSeasons: ArcadeSeason[] = []
) => arcadeSeasons.findLast(season => season.season_status === status)

export const findRelevantSeason = (arcadeSeasons: ArcadeSeason[] = []) => {
  const currentSeason = findFirstSeasonByStatus('current', arcadeSeasons)
  if (currentSeason) return currentSeason

  const nextSeason = findFirstSeasonByStatus('next', arcadeSeasons)
  if (nextSeason) return nextSeason

  return findLastSeasonByStatus('past', arcadeSeasons)
}

export const findSeasonById = (
  seasonId: number | string,
  arcadeSeasons: ArcadeSeason[] = []
) => arcadeSeasons.find(arcadeSeason => arcadeSeason.id.toString() === seasonId)
